// ── Template export helpers (capture + download) ─────────────────────────────
// First-page grid PNG + two-file download (no zip). See Docs/TEMPLATE_SYSTEM.md §7.1.
//
// Capture uses html-to-image (already installed) rather than html2canvas: it
// serializes the node into an SVG <foreignObject> that the browser lays out
// itself, so antd tables, Highcharts SVG and remote images render faithfully —
// html2canvas re-implements CSS by hand and blanks complex widgets (tables/logos).

import { toBlob } from "html-to-image";
import { GRID_MAX_WIDTH_PX, pageHeightPx } from "../PDF/pdfLayout";

/** Rasterize the first page of the live dashboard grid to a PNG blob. */
export async function captureFirstPagePng(): Promise<Blob | null> {
  const node = document.querySelector(".dashboard-grid-container") as HTMLElement | null;
  if (!node) return null;
  // Match the natural rendered width so the grid doesn't reflow in the clone;
  // clip height to one page so the thumbnail is the dashboard's first screen.
  const width = node.offsetWidth || GRID_MAX_WIDTH_PX;
  const height = Math.min(node.scrollHeight || Infinity, Math.round(pageHeightPx(width)));
  return await toBlob(node, {
    width,
    height,
    backgroundColor: "#ffffff",
    pixelRatio: 1,
    cacheBust: true,
    // Drop the dashed page-break guides from the thumbnail.
    filter: (el) =>
      !(el instanceof HTMLElement && typeof el.className === "string" && el.className.includes("page-break")),
  });
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** Download the template.json and preview.png as two separate files (dev assembles the folder). */
export function downloadTemplateFiles(
  workflow: string,
  templateJson: object,
  png: Blob | null,
): void {
  const jsonBlob = new Blob([JSON.stringify(templateJson, null, 2)], { type: "application/json" });
  downloadBlob(jsonBlob, `${workflow}.json`);
  // Fire the PNG on the next tick so the browser doesn't coalesce/block the 2nd download.
  if (png) setTimeout(() => downloadBlob(png, `${workflow}.png`), 0);
}
